import { useState, useEffect } from 'react'
import { api, type MSP, type MspMember } from '../api/client'

export default function SettingsPage({ msp }: { msp: MSP | null }) {
  const [credentials, setCredentials] = useState<{
    exists: boolean;
    clientId?: string;
    scopes?: string[];
    createdAt?: string;
    enabled?: boolean;
  } | null>(null)
  const [newSecret, setNewSecret] = useState<{ clientId: string; clientSecret: string } | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const [members, setMembers] = useState<MspMember[]>([])
  const [membersLoading, setMembersLoading] = useState(true)

  useEffect(() => {
    api.getCredentials()
      .then(data => setCredentials(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))

    api.getMspMembers()
      .then(res => setMembers(res.data))
      .catch(() => {}) // silently fail; member list is non-critical
      .finally(() => setMembersLoading(false))
  }, [])

  const handleCreate = async () => {
    setActionLoading(true)
    setError(null)
    try {
      const result = await api.createCredentials()
      setNewSecret({ clientId: result.clientId, clientSecret: result.clientSecret })
      setCredentials({ exists: true, clientId: result.clientId, scopes: result.scopes, enabled: true })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create credentials')
    } finally {
      setActionLoading(false)
    }
  }

  const handleRegenerate = async () => {
    if (!confirm('This will invalidate the current client secret. Any integrations using the old secret will stop working. Continue?')) {
      return
    }
    setActionLoading(true)
    setError(null)
    try {
      const result = await api.regenerateCredentials()
      setNewSecret({ clientId: result.clientId, clientSecret: result.clientSecret })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate credentials')
    } finally {
      setActionLoading(false)
    }
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white tracking-tight">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your MSP account and API credentials</p>
      </div>

      {/* MSP Info */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">MSP Account</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">MSP ID</span>
            <span className="text-sm text-white font-mono">{msp?.mspId || '-'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Name</span>
            <span className="text-sm text-white">{msp?.name || '-'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Slug</span>
            <span className="text-sm text-white font-mono">{msp?.slug || '-'}</span>
          </div>
        </div>
      </div>

      {/* API Credentials */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">API Credentials</h2>
        <p className="text-sm text-slate-400 mb-4">
          Use these credentials with the Partner API via OAuth2 client_credentials grant.
        </p>

        {loading ? (
          <p className="text-slate-500 text-sm">Loading credentials...</p>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        ) : !credentials?.exists ? (
          <div>
            <p className="text-slate-400 text-sm mb-4">
              No API credentials have been generated yet. Create credentials to access the Partner API programmatically.
            </p>
            <button
              onClick={handleCreate}
              disabled={actionLoading}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/50 text-slate-900 font-medium rounded-lg transition-colors text-sm"
            >
              {actionLoading ? 'Generating...' : 'Generate API Credentials'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Client ID (always visible) */}
            <div>
              <label className="block text-xs text-slate-500 mb-1">Client ID</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-800 text-slate-200 px-3 py-2 rounded-lg text-sm font-mono">
                  {credentials.clientId}
                </code>
                <button
                  onClick={() => copyToClipboard(credentials.clientId!, 'clientId')}
                  className="px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors text-sm"
                >
                  {copied === 'clientId' ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Scopes */}
            <div>
              <label className="block text-xs text-slate-500 mb-1">Scopes</label>
              <div className="flex gap-2 flex-wrap">
                {credentials.scopes?.map(scope => (
                  <span key={scope} className="px-2 py-1 bg-slate-800 text-slate-300 rounded text-xs font-mono">
                    {scope}
                  </span>
                ))}
              </div>
            </div>

            {/* Regenerate */}
            <div className="pt-4 border-t border-slate-800">
              <button
                onClick={handleRegenerate}
                disabled={actionLoading}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-800/50 text-slate-300 font-medium rounded-lg transition-colors text-sm"
              >
                {actionLoading ? 'Regenerating...' : 'Regenerate Client Secret'}
              </button>
              <p className="text-xs text-slate-500 mt-2">
                This will invalidate the current secret. Store the new secret securely.
              </p>
            </div>
          </div>
        )}

        {/* Show new secret banner */}
        {newSecret && (
          <div className="mt-6 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div className="flex-1">
                <p className="text-amber-300 font-medium text-sm mb-3">Save your client secret now. It will not be shown again.</p>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Client ID</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-slate-900/80 text-slate-200 px-3 py-2 rounded text-sm font-mono break-all">
                        {newSecret.clientId}
                      </code>
                      <button
                        onClick={() => copyToClipboard(newSecret.clientId, 'newClientId')}
                        className="px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors text-xs shrink-0"
                      >
                        {copied === 'newClientId' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Client Secret</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-slate-900/80 text-amber-300 px-3 py-2 rounded text-sm font-mono break-all">
                        {newSecret.clientSecret}
                      </code>
                      <button
                        onClick={() => copyToClipboard(newSecret.clientSecret, 'newSecret')}
                        className="px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors text-xs shrink-0"
                      >
                        {copied === 'newSecret' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Team Members */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mt-6">
        <h2 className="text-lg font-semibold text-white mb-4">Team Members</h2>
        <p className="text-sm text-slate-400 mb-4">
          People who have logged in to this MSP account.
        </p>

        {membersLoading ? (
          <p className="text-slate-500 text-sm">Loading members...</p>
        ) : members.length === 0 ? (
          <p className="text-slate-500 text-sm">No team members found.</p>
        ) : (
          <div className="space-y-3">
            {members.map(member => (
              <div key={member.email} className="flex items-center gap-3 py-3 border-b border-slate-800/50 last:border-0">
                {member.picture ? (
                  <img src={member.picture} alt="" className="w-9 h-9 rounded-full" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-sm font-medium text-white">
                    {member.name?.split(' ').map(w => w[0]).join('').slice(0, 2) || '?'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{member.name || member.email}</p>
                  <p className="text-xs text-slate-500 truncate">{member.email}</p>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="px-2 py-0.5 bg-slate-800 rounded text-slate-400 capitalize">
                    {member.provider}
                  </span>
                  <span title={new Date(member.lastLoginAt).toLocaleString()}>
                    {new Date(member.lastLoginAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
